import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { CalendarDays, ArrowRight, ArrowLeft, BookOpen } from "lucide-react";

export function ReadingPlansList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reading-plans").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="fade-in">
      <header className="mb-10">
        <div className="text-xs uppercase tracking-[0.2em] text-primary/80 font-medium mb-3">Reading Plans</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-stone-900">A daily rhythm with Scripture.</h1>
        <p className="mt-4 text-lg text-stone-700 max-w-2xl leading-relaxed">
          Follow a plan through a book, a theme, or the whole Bible — one day at a time.
        </p>
      </header>

      {loading ? (
        <div className="text-stone-500" data-testid="plans-loading">Loading plans…</div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-stone-300 bg-white p-12 text-center" data-testid="plans-empty">
          <CalendarDays className="h-10 w-10 text-stone-400 mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-serif text-2xl text-stone-900">Coming soon</h3>
          <p className="mt-2 text-stone-600 max-w-md mx-auto">Reading plans will appear here once published.</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2" data-testid="plans-grid">
          {items.map((p) => (
            <Link key={p.id} to={`/plans/${p.id}`} data-testid={`plan-card-${p.id}`}
              className="group flex overflow-hidden rounded-md border border-stone-200 bg-white transition-all hover:-translate-y-1 hover:border-primary min-h-[180px]">
              {p.image_url && (
                <div className="relative w-40 shrink-0 overflow-hidden">
                  <img src={p.image_url} alt="" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                </div>
              )}
              <div className="flex-1 p-6">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">{p.duration_days} days</div>
                <h3 className="font-serif text-2xl text-stone-900 leading-tight">{p.title}</h3>
                <p className="mt-2 text-stone-600 leading-relaxed line-clamp-3">{p.description}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                  Start plan <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.5} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function ReadingPlanDetail() {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/reading-plans/${id}`).then((r) => setPlan(r.data)).catch((e) => setErr(formatApiError(e))).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-stone-500">Loading…</div>;
  if (err) return <div className="text-destructive">{err}</div>;
  if (!plan) return null;

  const days = (plan.days || []).slice().sort((a, b) => a.day - b.day);

  return (
    <div className="fade-in" data-testid="plan-detail">
      <Link to="/plans" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-primary mb-6">
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> All plans
      </Link>

      <header className="mb-8 max-w-3xl">
        <div className="text-xs uppercase tracking-[0.2em] text-primary/80 font-medium mb-3">{plan.duration_days}-day plan</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-stone-900">{plan.title}</h1>
        <p className="mt-4 text-lg text-stone-700 leading-relaxed">{plan.description}</p>
      </header>

      {days.length === 0 ? (
        <div className="text-stone-500 rounded-md border border-dashed border-stone-300 bg-white p-8 text-center">
          Daily readings have not been added yet.
        </div>
      ) : (
        <div className="rounded-md border border-stone-200 bg-white overflow-hidden divide-y divide-stone-200">
          {days.map((d) => (
            <div key={d.day} className="flex items-start gap-4 p-4 hover:bg-stone-50 transition-colors" data-testid={`plan-day-${d.day}`}>
              <div className="w-14 shrink-0 text-center">
                <div className="text-xs uppercase tracking-widest text-stone-500">Day</div>
                <div className="font-serif text-2xl text-stone-900">{d.day}</div>
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/bible?ref=${encodeURIComponent(d.reference)}`} className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
                  <BookOpen className="h-4 w-4" strokeWidth={1.5} /> {d.reference}
                </Link>
                {d.note && <div className="mt-1 text-sm text-stone-600 leading-relaxed">{d.note}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
